import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery, type QueryKey } from '@tanstack/react-query';
import { ApiErrorException } from '@/api/client';

/**
 * W6: server cursor pages driven by the shared DataTable (page index +
 * Précédent / Suivant). Keeps the cursor of every page visited; a new filter
 * (a new `key`) goes back to the first page, and so does a cursor the server
 * refuses (INVALID_CURSOR: issued for other facets, or expired).
 */
export interface CursorPageResult<T> { items: T[]; next_cursor: string | null; total: number }

export function useCursorPages<T>(
  key: QueryKey,
  fetchPage: (cursor: string | undefined, signal: AbortSignal) => Promise<CursorPageResult<T>>,
  options: { refetchInterval?: (data: CursorPageResult<T> | undefined) => number | false } = {},
) {
  const [page, setPage] = useState(0);
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const keyString = JSON.stringify(key);
  useEffect(() => { setPage(0); setCursors([undefined]); }, [keyString]);

  const query = useQuery({
    queryKey: [...key, 'cursor', cursors[page] ?? null],
    queryFn: ({ signal }) => fetchPage(cursors[page], signal),
    placeholderData: keepPreviousData,
    refetchInterval: options.refetchInterval ? (q) => options.refetchInterval!(q.state.data) : undefined,
  });

  const invalidCursor = query.error instanceof ApiErrorException && query.error.code === 'INVALID_CURSOR';
  useEffect(() => {
    if (invalidCursor && page > 0) { setPage(0); setCursors([undefined]); }
  }, [invalidCursor, page]);

  const onPageChange = (next: number) => {
    if (next > page) {
      const c = query.data?.next_cursor;
      if (!c) return;
      setCursors((prev) => { const copy = prev.slice(0, page + 1); copy[page + 1] = c; return copy; });
    }
    setPage(next);
  };

  return { query, page, onPageChange };
}
